import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { LinkTarget } from './types'
import { SmartLink } from './SmartLink'
import { useStickyClass } from '../hooks/useStickyClass'

/**
 * **The top bar is gone (2026-09-02).** The template put a strip above every home page
 * carrying a city name and the words "Follow us:" with one icon after them. It cost a
 * band of vertical space on the first screen of all four apps and said nothing the footer
 * does not say better, so the `withTopBar` variant, the `topBar` prop and
 * `AppShell`'s `topBarRoutes` were all removed with it. `.ul-header-top` and
 * `.ul-header-2` remain in `_header.scss`, commented, because that sheet's order is
 * load-bearing and a rule left in place is harmless where a deleted one is not easy to
 * find again.
 */
export interface HeaderProps {
  /**
   * Set on a page that opens straight into pale content instead of a dark banner. The
   * bar is absolutely positioned over that first section, and its links are white for a
   * banner, so they have to darken here. Pair it with `.ul-plain-page-top` on the first
   * section, which reserves the bar's height.
   */
  onLight?: boolean
  /**
   * `ink` renders the dark-first bar: opaque and fixed from the first pixel, with the CTA
   * as a signal pill. The light bar floats transparently over a banner photograph and
   * only acquires a ground once `.sticky` lands, which has nothing to do on an app whose
   * whole page is already dark. See `.ul-header--ink` in `_header.scss`.
   */
  tone?: 'light' | 'ink'
  logo: string
  /**
   * The same lockup drawn pale, for the state where the bar floats over a photograph.
   *
   * The bar is transparent at rest and its links are white, because the first section of a
   * page is normally a dark banner — and the mark has to make the same journey the links
   * do. Without this the ink lockup sits on a dark hero at about 2:1.
   *
   * Optional: an app that does not pass it keeps the dark mark in every state, which is
   * correct for an app whose pages all open pale. It **replaces** `filter: brightness(0)
   * invert(1)`, which `_header.scss` used on the ink shell and its own comment called a
   * stopgap — that flattens a two-tone mark to a silhouette, where this is the artwork.
   */
  logoPale?: string
  /**
   * The venture's name, set under the logo — "Garage", "Resort", "Partners".
   *
   * The mark is the Foundation's, and every venture shares it. Without a word beneath it
   * the four apps have identical headers and a visitor cannot tell which of them they are
   * on from the one element they look at first. The Foundation itself passes nothing: it
   * IS King of Kings, and labelling it would be redundant.
   */
  logoLabel?: string
  homeTo: string
  /** Rendered inside `.ul-header-nav-wrapper`; pass `null` when the nav belongs to the sidebar. */
  nav?: ReactNode
  /**
   * The one button in the bar. Either an in-app route (`to`) or another KKF app (`href`)
   * — the Foundation's is "Donate" to its own page, while a venture's points at the
   * Foundation's donation page on its own domain.
   */
  cta?: LinkTarget & { label: string }
  /**
   * An extra control between the search button and the CTA — the garage hangs its cart
   * badge here.
   *
   * A slot rather than a `cart` prop, because the design system must not know that one of
   * the four apps sells things. What goes in it is the app's business; the bar only owes
   * it a place to sit.
   */
  actions?: ReactNode
  /**
   * Whether the bar carries the search opener.
   *
   * Defaults to **true**, so an app that says nothing renders the bar it always had. The
   * Foundation passes `false` (2026-09-19): its search modal only ever searched the page
   * a visitor was already on, and a magnifier that opens a field which cannot find
   * anything is worse than no magnifier — it is a promise the site does not keep.
   *
   * `onOpenSearch` becomes optional with it. `AppShell` is what decides not to mount the
   * modal at all in that case; a control nothing can open and a modal nothing opens are
   * two halves of the same removal, and leaving the second behind ships dead weight.
   */
  showSearch?: boolean
  onOpenSearch?: () => void
  onOpenSidebar: () => void
}

export function Header({
  onLight = false,
  tone = 'light',
  logo,
  logoPale,
  logoLabel,
  homeTo,
  nav = null,
  cta,
  actions,
  showSearch = true,
  onOpenSearch,
  onOpenSidebar,
}: HeaderProps) {
  const stuck = useStickyClass()
  // `onLight` darkens a transparent bar's links over pale content — a problem the ink bar
  // does not have, since it carries its own opaque ground. Applying both would be
  // contradictory, so the tone wins.
  const classes = ['ul-header', tone === 'ink' ? 'ul-header--ink' : onLight ? 'ul-header--on-light' : '']
    .filter(Boolean)
    .join(' ')

  return (
    <header className={classes}>
      <div className={`ul-header-bottom to-be-sticky${stuck ? ' sticky' : ''}`}>
        <div className="ul-header-bottom-wrapper ul-header-container">
          <div className="logo-container">
            <Link to={homeTo} className="ul-logo-lockup">
              {/* The alt carries the venture, because the label under the mark is styled
                  text and a screen reader should hear one name, not "logo" then "Garage". */}
              {/*
                Pale over a photograph, ink once there is a pale ground under the bar —
                sticky, or a route that opens into content. The same condition the links
                already follow, read from the same two flags.
              */}
              <img
                src={logoPale && !onLight && !stuck ? logoPale : logo}
                alt={logoLabel ? `King of Kings ${logoLabel}` : 'King of Kings'}
                className="logo"
              />
              {logoLabel && (
                <span className="ul-logo-lockup-label" aria-hidden="true">
                  {logoLabel}
                </span>
              )}
            </Link>
          </div>

          {/* header nav */}
          <div className="ul-header-nav-wrapper">{nav}</div>

          {/* actions */}
          <div className="ul-header-actions">
            {showSearch && (
              <button className="ul-header-search-opener" onClick={onOpenSearch} aria-label="Open search">
                <i className="flaticon-search" />
              </button>
            )}
            {actions}
            {cta && (
              <SmartLink to={cta.to} href={cta.href} className="ul-btn d-sm-inline-flex d-none">
                <i className="flaticon-fast-forward-double-right-arrows-symbol" /> {cta.label}{' '}
              </SmartLink>
            )}
            <button
              className="ul-header-sidebar-opener d-lg-none d-inline-flex"
              onClick={onOpenSidebar}
              aria-label="Open menu"
            >
              <i className="flaticon-menu" />
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}
